import type {
  Card,
  MarketOption,
  RawCard,
  RawMarketOption,
  RawSport,
  RawTab,
  Sport,
  Tab,
  Token,
} from "./eventMarketsTypes";

export function parseTokenFromStorage(tokenString: string | null): Token | null {
  if (!tokenString) return null;
  try {
    return JSON.parse(tokenString) as Token;
  } catch {
    return null;
  }
}

export function mapRawSportsToSports(raw: RawSport[]): Sport[] {
  return raw.map((s) => ({ id: s.sport_id, name: s.name }));
}

export function mapRawTabsToTabs(items: RawTab[]): Tab[] {
  return items.map((t, idx) => {
    const name = t.display_name || t.name || "";
    return {
      entity_id: t.entity_id,
      tab_id:
        t.entity_id?.split("#")[2] || name.toLowerCase() || `tab_${idx}`,
      name,
      order: Number(t.display_order ?? idx),
      card_count: t.card_count || 0,
      is_active: t.is_active,
      translations: t.translations || null,
    };
  });
}

export function sortTabsByOrder(tabs: Tab[]): Tab[] {
  return [...tabs].sort((a, b) => a.order - b.order);
}

export function tabsListToSignature(tabs: Tab[]): string {
  return tabs.map((t) => `${t.tab_id}:${t.order}:${t.card_count}`).join("|");
}

export function buildTabOrderMap(tabs: Tab[]): Map<string, number> {
  const tabOrderMap = new Map<string, number>();
  tabs.forEach((tab, index) => {
    tabOrderMap.set(
      tab.tab_id,
      typeof tab.order === "number" ? tab.order : index + 1
    );
  });
  return tabOrderMap;
}

export function mapRawCardsForTab(
  items: RawCard[],
  tab: Pick<Tab, "tab_id" | "name">,
  tabOrderMap: Map<string, number>,
  tabIndex: number
): Card[] {
  return items.map((c, idx) => ({
    entity_id: c.entity_id,
    market_id: c.market_id,
    name: c.name,
    display_name: c.display_name || c.name,
    order: Number(c.display_order ?? idx + 1),
    is_active: c.is_active,
    tab_id: tab.tab_id,
    tab_name: tab.name,
    tab_order: tabOrderMap.get(tab.tab_id) ?? tabIndex + 1,
    translations: c.translations || null,
  }));
}

export function sortAggregatedCards(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => {
    if (a.tab_order === b.tab_order) return a.order - b.order;
    return a.tab_order - b.tab_order;
  });
}

export function mapRawMarketOptionsToMarketOptions(
  items: RawMarketOption[],
  activeSportId: Sport["id"]
): MarketOption[] {
  return items.map((i) => ({
    entity_id:
      i.entity_id || `MKT#${String(activeSportId)}#${String(i.market_id)}`,
    market_id: i.market_id,
    item_name: i.display_name || i.name || "",
    item_type: "MARKET" as const,
  }));
}

export function groupCardsByTabId(cards: Card[]): Map<string, Card[]> {
  const byTab = new Map<string, Card[]>();
  for (const card of cards) {
    const list = byTab.get(card.tab_id);
    if (list) list.push(card);
    else byTab.set(card.tab_id, [card]);
  }
  return byTab;
}

export function cardRowDisplayOrder(card: Card, rowIndex: number): number {
  return typeof card.order === "number" ? card.order : rowIndex + 1;
}
